<template>
  <div>
    <div
      :class="$style.sectionRoot"
      data-walkthrough="main-user-details-form"
    >
      <h2 :class="$style.sectionHeader">
        User Information
      </h2>
      <p
        v-if="item.externally_managed"
        :class="$style.externallyManagedMessage"
      >
        This user's information is managed in Active Directory &trade;.
      </p>
      <div :class="$style.sectionContent">
        <div :class="$style.inputRow">
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="item.firstname"
              :config="{
                attribute: 'firstname',
                disabled: isRestrictedField('firstname'),
                label: 'First Name',
                name: 'First Name',
                value: item.firstname,
              }"
              @InputValidatorModel="handleValueChange"
            />
          </div>
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="item.middlename"
              :config="{
                attribute: 'middlename',
                disabled: isRestrictedField('middlename'),
                label: 'Middle Name',
                name: 'Middle Name',
                value: item.middlename,
              }"
              @InputValidatorModel="handleValueChange"
            />
          </div>
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="item.lastname"
              :config="{
                attribute: 'lastname',
                disabled: isRestrictedField('lastname'),
                label: 'Last Name',
                name: 'Last Name',
                value: item.lastname,
              }"
              @InputValidatorModel="handleValueChange"
            />
          </div>
        </div>
        <div :class="$style.inputRow">
          <div
            :class="[$style.flexSingleWidth, $style.inputTooltipWrapper]"
            data-walkthrough="user-aside-username-input-container"
          >
            <InputValidator
              v-model="item.username"
              :config="{
                attribute: 'username',
                dataLpignore: 'true',
                label: '* Username',
                name: 'Username',
                disabled: isUsernameDisabled,
                validate: 'required',
                value: item.username,
              }"
              :tooltip="$options.tooltips.username"
              @InputValidatorModel="handleValueChange"
            />
            <div
              v-if="isLocalUserAccountDisabled"
              :class="$style.inputTooltipContainer"
            >
              <div :class="$style.inputTooltopChevron" />
              <p>Username can only be modified if a user is not bound to any resources.</p>
            </div>
          </div>
          <div
            v-if="showLocalUserAccountButton"
          >
            <FlatActionButton
              :action="handleAddLocalUserAccount"
              :buttonClass="['btnDefault', 'btnCTA']"
              :class="$style.addLocalUserAccount"
              :disabled="isLocalUserAccountDisabled"
              :iconComponent="IconPlus"
              text="local user account"
            />
            <IconTooltip
              :class="$style.addLocalUserAccountToolTip"
            >
              <MarkdownInline :mdTemplate="$options.tooltips.addLocalUserAccount.text" />
            </IconTooltip>
          </div>
          <div
            v-else
            :class="[$style.flexSingleWidth, $style.inputTooltipWrapper]"
          >
            <InputValidator
              ref="localUserAccountInput"
              v-model="item.systemUsername"
              :config="{
                attribute: 'systemUsername',
                dataLpignore: 'true',
                label: 'Local User Account',
                name: 'Local User Account',
                disabled: isLocalUserAccountDisabled,
                validate: 'isNotUsername',
                value: item.systemUsername,
              }"
              :tooltip="$options.tooltips.addLocalUserAccount"
              @InputValidatorModel="handleValueChange"
            />
            <div
              v-if="isLocalUserAccountDisabled"
              :class="$style.inputTooltipContainer"
            >
              <div :class="$style.inputTooltopChevron" />
              <p>
                Local User Account can only be modified if a user is not
                bound to any resources.
              </p>
            </div>
          </div>
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="item.displayname"
              :config="{
                attribute: 'displayname',
                disabled: isRestrictedField('displayname'),
                label: 'Display Name',
                name: 'Display Name',
                value: item.displayname,
              }"
              :tooltip="$options.tooltips.displayName"
              @InputValidatorModel="handleValueChange"
            />
          </div>
        </div>
        <div
          :class="$style.inputRow"
          data-walkthrough="user-aside-username-email-container"
        >
          <InputValidator
            v-model="item.managedAppleId"
            :config="{
              attribute: 'managedAppleId',
              disabled: isRestrictedField('managedAppleId'),
              label: 'Managed Apple ID',
              name: 'Managed Apple ID',
              validate: 'email',
              value: item.managedAppleId,
            }"
            :tooltip="$options.tooltips.managedAppleId"
            @InputValidatorBlur="handleBlur(
              item.managedAppleId, 'managedAppleId', 'Managed Apple ID'
            )"
            @InputValidatorModel="handleValueChange"
          />
          <InputValidator
            v-model="item.email"
            :config="{
              attribute: 'email',
              disabled: isRestrictedField('email'),
              label: '* Company Email',
              name: 'Company Email',
              validate: 'required|email',
              value: item.email,
            }"
            :tooltip="$options.tooltips.companyName"
            @InputValidatorBlur="handleBlur(item.email, 'email', 'Company Email')"
            @InputValidatorModel="handleValueChange"
          />
          <InputValidator
            v-model="item.alternateEmail"
            :config="{
              attribute: 'alternateEmail',
              disabled: isRestrictedField('alternateEmail'),
              label: 'Alternate Email',
              name: 'Alternate Email',
              validate: getAlternateEmailValidation,
              value: item.alternateEmail,
            }"
            @InputValidatorBlur="handleBlur(
              item.alternateEmail,
              'alternateEmail',
              'Alternate Email')"
            @InputValidatorModel="handleValueChange"
          />
        </div>
        <div :class="$style.inputRow">
          <div :class="$style.flexSingleWidth">
            <TextareaValidator
              v-model="item.description"
              :config="{
                attribute: 'description',
                disabled: isRestrictedField('description'),
                label: 'Description',
                name: 'Description',
                validate: 'max:1024',
                value: item.description,
              }"
              :tooltip="$options.tooltips.description"
              @InputValidatorModel="handleValueChange($event.trim(), 'description')"
            />
          </div>
        </div>
        <div
          v-if="isItemBeingUpdated === true"
          :class="$style.readOnlyFields"
        >
          <div
            :class="[$style.infoContainer, $style.oneThird]"
          >
            <p :class="$style.infoTitle">
              Creation date:
            </p>
            <p>{{ item.created }}</p>
          </div>
          <div
            :class="[$style.infoContainer, $style.twoThirds]"
          >
            <p :class="$style.infoTitle">
              LDAP Distinguished Name
            </p>
            <p>{{ ldapDN(item.username) }}</p>
          </div>
        </div>
        <InformationalText
          v-if="!systemUsersCanEdit"
          :text="getUserFieldsReadOnlyText()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ItemTypeConsts, LocalStorageConsts, StoreModelMap } from 'client/util/Constants';
import { MarkdownInline } from '@jumpcloud/ui-components';
import { mapGetters } from 'vuex';
import AddLocalUserAccountModal from 'client/systemusers/UserDetails/AddLocalUserAccountModal.vue';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconPlus from 'public/img/icons/plus.svg?inline';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import InformationalText from 'client/components/ContentSection/InformationalText.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'components/Modal/ModalEventBus';
import TextareaValidator from 'client/components/FormValidation/TextareaValidator.vue';
import UserDetailsMethodsMixin from 'client/systemusers/UserDetails/mixins/UserDetailsMethodsMixin';
import ldapDnMixin from 'client/systemusers/UserDetails/mixins/LDAPDistinguishedNameMixin';

const storeModel = StoreModelMap[ItemTypeConsts.systemUser];

const { addLocalUserAccountModalAcknowledged } = LocalStorageConsts;

export default {
  name: 'MainUserDetailsForm',

  components: {
    FlatActionButton,
    IconTooltip,
    InformationalText,
    InputValidator,
    MarkdownInline,
    TextareaValidator,
  },

  inject: ['$validator'],

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    UserDetailsMethodsMixin,
    ldapDnMixin,
  ],

  tooltips: {
    companyName: {
      text: 'Company Email is the email address JumpCloud uses for all user communications.',
    },
    description: {
      text: 'Description can be used to capture user details that are not documented under other attributes. This attribute propagates to LDAP for any syncing users.',
    },
    displayName: {
      text: 'Display Name specifies the user’s preferred full name. This name is stored by JumpCloud but is not used in user communications.',
    },
    managedAppleId: {
      text: `Allows iOS users to access Apple services, such as iCloud and iWork. A Managed Apple ID is required to enroll a personal iOS device in MDM, and cannot be used to purchase content. You can run a script to upload a large number of MAIDs into JumpCloud. <a href="${KbArticleUrls.maidScript}" rel="noopener noreferrer" target="_blank"><strong>Learn more</strong></a>`,
    },
    username: {
      text: `Username can only be modified if a user is not bound to any resources. <a href="${KbArticleUrls.unbindingUsers}" rel="noopener noreferrer" target="_blank">Learn more</a> about the potential impacts of removing resource bindings to make username modifications.`,
      kbLink: KbArticleUrls.userNamingConvention,
      kbLinkText: 'View Naming Conventions for Users Guide',
    },
    addLocalUserAccount: {
      text: `If you want to take over a current account on a system then you can use this local user account to match that local name before you bind. This field can't be edited when bound to a system. [Learn more](${KbArticleUrls.localUserAccount})`,
    },
  },

  vValidateDict: {
    custom: {
      'Alternate Email': {
        email: '* Please enter a valid email address.',
        is_not: '* Must be different than Company Email.',
      },
      'Company Email': {
        email: '* Please enter a valid email address.',
        required: '* Please enter a valid email address.',
      },
      'Managed Apple ID': {
        email: '* Please enter a valid email address.',
      },
      Description: {
        max: '* 1,024 character limit.',
      },
      Username: {
        required: '* Username is empty.',
      },
      'Local User Account': {
        is_not: '* Must be different than Username',
      },
    },
  },

  props: {
    handleValueChange: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      addLocalUserAccountModalAcknowledged: '',
      IconPlus,
      systemUsernameInitiallySet: false,
    };
  },

  computed: {
    ...mapGetters(storeModel, ['hasBindings', 'hasSystemBindings', 'isRestrictedField']),
    ...mapGetters('SettingsModule', ['systemUsersCanEdit']),
    ...mapGetters('FeatureFlagsModule', ['betaFeatures']),

    getAlternateEmailValidation() {
      return `email|is_not:${this.item.email}`;
    },

    isLocalUserAccountDisabled() {
      // Admin may change the local user account if the user is not bound to anything
      if (this.hasSystemBindings === undefined) {
        // If hasSystemBindings is undefined, it has not been determined yet
        //  whether the local user account is bound
        return true;
      }

      return this.hasSystemBindings;
    },

    isUsernameDisabled() {
      // Admin may not change the username of an AD user
      if (this.item.externally_managed === true) {
        return true;
      }

      // Admin may change the username if a new user is being created
      if (this.isItemBeingUpdated === false) {
        return false;
      }

      // Admin may change the username if the user is not bound to anything
      if (this.hasBindings === undefined) {
        // If hasBindings is undefined, it has not been determined yet whether the user is bound
        return true;
      }
      return this.hasBindings;
    },

    showLocalUserAccountButton() {
      if (this.systemUsernameInitiallySet) {
        return false;
      }
      return !this.addLocalUserAccountModalAcknowledged;
    },
  },

  created() {
    // eslint-disable-next-line max-len
    this.addLocalUserAccountModalAcknowledged = localStorage.getItem(addLocalUserAccountModalAcknowledged);
    if (this.item) {
      this.systemUsernameInitiallySet = !!this.item.systemUsername;
    }
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  mounted() {
    this.$validator.localize('en', this.$options.vValidateDict);

    this.$validator.extend('isNotUsername', {
      getMessage: '* Must be different than Username',
      validate: value => this.isNotUsername(value),
    });
  },

  methods: {
    handleAddLocalUserAccount() {
      EventHelper.triggerLocalUserAccountModalClicked();
      ModalEventBus.open({
        component: AddLocalUserAccountModal,
        componentProps: {
          handleMainAction: this.setAddLocalUserAccountModal,
        },
        isLargeModal: true,
      });
    },

    handleBlur(value, key, name) {
      if (value === null || value === undefined) {
        // eslint-disable-next-line no-param-reassign
        value = '';
      }
      this.handleValueChange(String(value).trim(), key);
      this.$nextTick()
        .then(() => this.$validator.validate(name));
    },

    isNotUsername(systemUsername = '') {
      if (!this.item.username) return true;
      return systemUsername.toLowerCase() !== this.item.username.toLowerCase();
    },

    setAddLocalUserAccountModal() {
      localStorage.setItem(addLocalUserAccountModalAcknowledged, 'acknowledged');
      ModalEventBus.close();
      // eslint-disable-next-line max-len
      this.addLocalUserAccountModalAcknowledged = localStorage.getItem(addLocalUserAccountModalAcknowledged);
    },
  },
};
</script>

<style module>
@import '../../css/sections.css';
@import '../../css/flex.css';

@value jcInformationalBase from '~css/brand-colors.css';
@value max-width-x-large from '~css/breakpoints.css';

.addLocalUserAccount.addLocalUserAccount {
  margin-top: 32px;
}

.readOnlyFields {
  display: flex;
  margin-bottom: 10px;
}

.externallyManagedMessage {
  margin: 10px 18px 0 !important;
  padding: 0;
}

.infoContainer > p {
  font-size: 12px;
  font-weight: 400;
  line-break: anywhere;
  line-height: 1.2;
  margin-bottom: 4px;
}

.infoContainer > .infoTitle {
  font-weight: 600;
}

.inputTooltipWrapper {
  position: relative;
}

.inputTooltipContainer {
  background: jcInformationalBase;
  color: white;
  display: none;
  font-size: 10px;
  font-weight: 700;
  position: absolute;
  top: 90%;
  z-index: 10;
}

.inputTooltipWrapper:hover > .inputTooltipContainer {
  display: block;
}

.inputTooltipContainer p {
  margin: 0;
  padding: 0.5em 1em;
}

.inputTooltopChevron {
  border-bottom: 7px solid jcInformationalBase;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  height: 0;
  left: 50%;
  position: absolute;
  top: -7px;
  width: 0;
}
</style>
